import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { api } from "./axiosInstance";

const AcceptInvite = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [tokenPayload, setTokenPayload] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const token = params.get("token");
        if (!token) {
            setError("Token is missing");
            return;
        }
        try {
            const decoded = jwtDecode(token);
            setTokenPayload(decoded);
        } catch (err) {
            setError("Invalid token");
        }
    }, [location.search]);

    const handleAccept = async () => {
        setLoading(true);
        setError("");
        const params = new URLSearchParams(location.search);
        const token = params.get("token");
        try {
            const payload = { token };
            // For new invites, require a password
            if (!tokenPayload.company || !tokenPayload.role) {
                if (!password) {
                    setError("Password is required");
                    setLoading(false);
                    return;
                }
                if (password !== confirmPassword) {
                    setError("Passwords do not match");
                    setLoading(false);
                    return;
                }
                payload.password = password;
            }
            // Call the backend endpoint to accept the invite
            await api.post("/employer/invite/accept", payload);
            navigate("/signin");
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.error || "Failed to accept invitation");
        } finally {
            setLoading(false);
        }
    };

    const isNewInvite = tokenPayload && (!tokenPayload.company || !tokenPayload.role);

    if (!tokenPayload && !error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-darkblue text-lightgray">
                <p>Loading invitation details...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-darkblue text-lightgray">
            <div className="bg-bluegray p-6 rounded-lg shadow-lg w-full max-w-md">
                <h2 className="text-2xl font-bold text-center mb-4 text-orange">Accept Invitation</h2>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-600 text-white p-3 rounded mb-4">
                        {error}
                    </div>
                )}

                {/* Content */}
                {isNewInvite ? (
                    <>
                        <p>Please set your password to activate your account and accept the invitation.</p>
                        <div className="mt-4">
                            <input
                                type="password"
                                placeholder="New Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full p-2 mb-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-2 focus:ring-orange"
                            />
                            <input
                                type="password"
                                placeholder="Confirm Password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full p-2 mb-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-2 focus:ring-orange"
                            />
                        </div>
                    </>
                ) : (
                    <p className="mb-4">
                        You have been invited to update your company affiliation and role.
                        Click accept to proceed.
                    </p>
                )}

                {/* Button */}
                <button
                    onClick={handleAccept}
                    disabled={loading}
                    className="w-full mt-4 bg-orange text-darkblue font-bold py-2 rounded hover:bg-deeporange disabled:opacity-50"
                >
                    {loading ? "Processing..." : "Accept Invitation"}
                </button>
            </div>
        </div>
    );
};

export default AcceptInvite;
